import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
// Import Plyr CSS early so custom styles can override it
import 'plyr/dist/plyr.css';

// ============================================================================
// PLYR ERROR HANDLING SETUP
// ============================================================================
// Set up error handling for Plyr before it's dynamically imported in VideoPlayer
// Plyr is loaded dynamically (await import('plyr')), so this code executes first
// and ensures error handlers are in place before Plyr initializes

// Patch XMLHttpRequest to gracefully handle Plyr sprite loading errors
// This allows Plyr to attempt sprite loading but prevents errors from breaking the app
const originalXHROpen = XMLHttpRequest.prototype.open;
const originalXHRSend = XMLHttpRequest.prototype.send;

XMLHttpRequest.prototype.open = function(method, url, ...args) {
  this._url = url;
  this._method = method;
  return originalXHROpen.apply(this, [method, url, ...args]);
};

XMLHttpRequest.prototype.send = function(...args) {
  // Intercept Plyr sprite/icon loading requests specifically
  // Only handle requests to Plyr CDN to avoid interfering with other app requests
  if (this._url && (
    this._url.includes('cdn.plyr.io') || 
    (this._url.includes('plyr') && (this._url.endsWith('.svg') || this._url.endsWith('.png')))
  )) {
    // Wrap in try-catch to prevent any errors from propagating
    try {
      // Add error handler to prevent uncaught errors
      this.addEventListener('error', (event) => {
        console.warn('Plyr resource loading failed (handled gracefully):', this._url);
        // Prevent error from propagating
        event.stopPropagation();
        event.preventDefault();
      }, { once: true });
      
      // Add error handler for network failures
      this.addEventListener('loadend', () => {
        if (this.status >= 400) {
          console.warn('Plyr resource loading failed with status:', this.status, this._url);
        }
      }, { once: true });
      
      // Add onerror handler as additional safety
      this.onerror = (event) => {
        console.warn('Plyr XHR error (handled gracefully):', this._url);
        event.stopPropagation();
        event.preventDefault();
      };
    } catch (err) {
      // Silently catch any errors in setting up handlers
      console.warn('Error setting up Plyr XHR handlers:', err);
    }
  }
  
  try {
    return originalXHRSend.apply(this, args);
  } catch (error) {
    // Catch any errors during send for Plyr requests
    if (this._url && (this._url.includes('cdn.plyr.io') || this._url.includes('plyr'))) {
      console.warn('Plyr XHR send error (handled gracefully):', this._url, error);
      return;
    }
    throw error;
  }
};

// Global error handler to suppress XMLHttpRequest errors from Plyr
// These errors occur when Plyr tries to load external resources (icons, sprites)
// and can be safely ignored as Plyr will fall back to default behavior
// IMPORTANT: Only suppress errors that are clearly from Plyr (by filename or stack trace)
// Do NOT suppress generic XMLHttpRequest errors as they may be legitimate app errors
window.addEventListener('error', (event) => {
  // Only suppress errors that are clearly from Plyr vendor chunk
  // Check for Plyr-specific indicators: filename or stack trace containing Plyr references
  const isPlyrError = 
    event.filename?.includes('plyr') ||
    event.filename?.includes('vendors-node_modules_plyr') ||
    event.error?.stack?.includes('plyr') ||
    event.error?.stack?.includes('vendors-node_modules_plyr') ||
    // Only suppress XMLHttpRequest errors if they're also from Plyr context
    (event.error?.message?.includes('XMLHttpRequest') && 
     (event.filename?.includes('plyr') || 
      event.error?.stack?.includes('plyr') || 
      event.error?.stack?.includes('vendors-node_modules_plyr')));
  
  if (isPlyrError) {
    event.preventDefault();
    event.stopPropagation();
    console.warn('Plyr resource loading error (suppressed):', event.error?.message || event.message);
    return true;
  }
}, true);

// Also handle unhandled promise rejections from Plyr
// IMPORTANT: Only suppress rejections that are clearly from Plyr
window.addEventListener('unhandledrejection', (event) => {
  // Only suppress rejections that are clearly from Plyr
  // Check for Plyr-specific indicators in stack trace
  const isPlyrRejection = 
    event.reason?.stack?.includes('plyr') ||
    event.reason?.stack?.includes('vendors-node_modules_plyr') ||
    // Only suppress XMLHttpRequest rejections if they're also from Plyr context
    ((event.reason?.message?.includes('XMLHttpRequest') || 
      event.reason?.toString?.().includes('XMLHttpRequest')) &&
     (event.reason?.stack?.includes('plyr') || 
      event.reason?.stack?.includes('vendors-node_modules_plyr')));
  
  if (isPlyrRejection) {
    event.preventDefault();
    console.warn('Plyr promise rejection (suppressed):', event.reason?.message || event.reason);
    return true;
  }
});

// Create a client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);

// Register service worker for offline support
serviceWorkerRegistration.register();

reportWebVitals();